import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { HeadingText } from "../../utils/CustomTagComponents";
import VideoPlayer from "../common/VideoPlayer";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const AboutSection = ({
  title = "",
  desc = "",
  points = [],
  mediaType = "image",
  mediaLink = "",
  containerProps = {},
  defaultHeight = "auto",
  defaultImageHeight = 580,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const pointsRef = useRef(null);
  const descRef = useRef(null);

  const [descHeight, setDescHeight] = useState(defaultHeight);
  const [imgHeight, setImgHeight] = useState("auto");

  const fadeLeft = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  const fadeRight = {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  const [mediaRef, mediaInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [textRef, textInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // ✅ CLEAN HTML
  const cleanHTML = (html) => {
    if (!html) return "";
    return html
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/<p><br><\/p>/gi, "")
      .replace(/&nbsp;/gi, " ");
  };

  const getResponsiveHtml = (html) => {
    if (!html) return "";

    let clean = html
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/&nbsp;/gi, "")
      .replace(/\u00A0/gi, " ")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/\[empty_line\]/gi, "<br />");

    if (!isMobile) return clean;


    return clean.replace(/font-weight:\s*900/gi, "font-weight: 700").replace(/&nbsp;/gi, "").replace(/\u00A0/gi, " ").replace(/<br\s*\/?>/gi, "").replace(/\[empty_line\]/gi, "<br />");
  };

  // ✅ IMAGE HEIGHT MATCH CONTENT
  useEffect(() => {
    if (descRef.current) {
      const contentHeight = descRef.current.offsetHeight;
      setImgHeight(
        contentHeight > defaultImageHeight
          ? contentHeight
          : defaultImageHeight
      );
    }
  }, [desc, defaultImageHeight]);

  // ✅ DESC HEIGHT BASED ON POINTS
  useEffect(() => {
    if (pointsRef.current) {
      const pointsHeight = pointsRef.current.offsetHeight || 0;
      const adjustedHeight = Math.max(defaultHeight - pointsHeight - 30, 200);
      setDescHeight(adjustedHeight);
    } else {
      setDescHeight(defaultHeight);
    }
  }, [points]);

  const contentLength = desc?.length || 0;

  const getHeight = () => {
    if (contentLength < 1000) return { xs: 250, md: 400 };
    if (contentLength < 1400) return { xs: 300, md: 500 };
    return { xs: 350, md: 630 };
  };

  // ✅ CONTENT STYLING (KEY PART)
  const contentStyles = {
    fontSize: { xs: 14, sm: 15, md: 16 },
    lineHeight: 1.625,
    textAlign: "justify",

    "& p": {
      marginBlockStart: '0px !important',
      marginBottom: "16px",
    },

    "& p:last-of-type": {
      marginBottom: "0px",
    },

    // ✅ TARGET KEY-VALUE LINES (Language, Currency...)
    "& p:has(strong)": {
      position: "relative",
      paddingLeft: "20px",
      marginBottom: "8px",
      fontWeight: 500,
    },

    // ✅ ADD ICON BEFORE THEM
    "& p:has(strong)::before": {
      content: '"\\f101"',
      fontFamily: "FontAwesome",
      position: "absolute",
      left: 0,
      top: "2px",
      color: "#fd0000",
      fontSize: "14px",
    },

    // ✅ REMOVE DEFAULT BULLETS
    "& ul": {
      listStyle: "none",
      paddingLeft: "0px",
      marginBottom: "12px",
    },

    // ✅ CUSTOM LIST ITEM
    "& li": {
      position: "relative",
      paddingLeft: "28px",
      marginBottom: "4px",
      lineHeight: 1.7,
    },

    // ✅ FIRST ITEM
    "& li:first-of-type": {
      marginTop: "-10px",
    },

    // ✅ LAST ITEM
    "& li:last-of-type": {
      marginBottom: "15px",
    },

    // ✅ ADD ARROW ICON
    "& li::before": {
      content: '"\\f101"',
      fontFamily: "FontAwesome",
      position: "absolute",
      left: '5px',
      top: "2px",
      fontSize: "14px",
      color: "#fd0000",
    },

    "& h1, & h2, & h3, & h4": {
      marginTop: "20px",
      marginBottom: "12px",
      fontWeight: 600,
    },

    "& strong": {
      fontWeight: 600,
    },
  };


  return (
    <Box>
      <Container
        maxWidth="xl"
        sx={{
          overflow: "hidden",
          py: { xs: 2, sm: 2, md: 3 },
          px: { xs: 3, sm: 4, md: 6 },
          paddingLeft: { xs: "24px !important", md: "48px !important" },
          paddingRight: { xs: "24px !important", md: "48px !important" },
          ...containerProps.sx,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2fr 3fr" },
            alignItems: "center",
            gap: { xs: 3, md: 4 },
          }}
        >
          {/* ---------- LEFT IMAGE ---------- */}
          <Box
            component={motion.div}
            ref={mediaRef}
            initial="hidden"
            animate={mediaInView ? "visible" : "hidden"}
            variants={fadeLeft}
            sx={{
              display: 'flex',
              justifyContent: { xs: "center", lg: "start" },
              alignItems: 'center',
              overflow: "hidden",
            }}
          >
            {mediaType === "video" ? (
              <VideoPlayer
                src={mediaLink}
                width="100%"
                maxheight={imgHeight || defaultImageHeight}
                iconSize={{ xs: 40, sm: 50, md: 60 }}
              />
            ) : (
              <Box
                component="img"
                src={mediaLink}
                alt="About Media"
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 400, md: 550 },
                  height: getHeight(),
                  objectFit: "cover",
                  borderRadius: 4,
                  display: "flex",
                }}
              />
            )}
          </Box>

          {/* ---------- RIGHT TEXT ---------- */}
          <Box
            component={motion.div}
            ref={textRef}
            initial="hidden"
            animate={textInView ? "visible" : "hidden"}
            variants={fadeRight}
          >
            {title && (
              <Box
                sx={{
                  display: "inline-block",
                  position: "relative",
                  mb: 2,

                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    bottom: -5,
                    width: "200px",
                    height: "2px",
                    backgroundColor: "#fd0000",
                    opacity: "0.5"
                  },
                }}
              >
                <HeadingText
                  sx={{
                    fontSize: { xs: "22px", sm: "26px", md: "30px" },
                    lineHeight: '1.3 !important',
                    fontWeight: 700,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getResponsiveHtml(title),
                  }}
                />
              </Box>
            )}

            {/* ---------- POINTS ---------- */}
            {points.length > 0 && (
              <Stack ref={pointsRef} spacing={1.5} mb={3}>
                {points.map((point, idx) => (
                  <Stack key={idx} direction="row" spacing={1}>
                    <Box sx={{ color: "#FFCC00", fontSize: "16px" }}>
                      ➜
                    </Box>
                    <Typography sx={{ fontSize: { xs: 14, sm: 16 } }}>
                      {point}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {/* ---------- DESCRIPTION ---------- */}
            <Typography
              ref={descRef}
              component="div"
              sx={contentStyles}
              dangerouslySetInnerHTML={{
                __html: cleanHTML(getResponsiveHtml(desc)),
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutSection;